import React from 'react';
import { createPortal } from 'react-dom';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, Plus, FileText } from 'lucide-react';
import { actsAsOwner } from './ownership';
import toast from 'react-hot-toast';
import Loader from '../../../shared/components/ui/Loader';
import noDataIcon from '../../../shared/assets/svg/NoData.svg';
import Modal from '../../../shared/components/ui/Modal';
import AddExpenseModal from '../Engineer/components/AddExpenseModal';
import {
    getTaskLogs,
    createSubTaskLogReply,
    getSubTaskExpenses,
    approveSubTaskExpense,
    voidSubTaskExpense
} from '../../services/api/implementation/impl';
import {
    evaluateSpendApprove,
    formatRupees,
    isExternalSession,
    lineExceedsAllocation,
    SPEND_OWNER_MESSAGE
} from './budget';

// Status and system entries plus comments against each log. New standalone
// notes are not added from this tab — comment on an existing row instead.
export const ACTIVITY_TABS = ['Logs', 'Expense', 'Documents'];

const spendBadge = (expense) => {
    const status = expense.ledgerStatus || (expense.paid ? 'paid' : 'draft');
    if (status === 'paid') return { label: 'Paid', className: 'bg-green-100 text-green-700' };
    if (status === 'approved') return { label: 'Approved', className: 'bg-teal-50 text-teal-800' };
    if (status === 'rejected') return { label: 'Rejected', className: 'bg-gray-100 text-gray-600' };
    if (status === 'void') return { label: 'Cancelled', className: 'bg-gray-100 text-gray-600' };
    return { label: 'Draft', className: 'bg-amber-50 text-amber-800' };
};

const getInitials = (name) =>
    (name || '')
        .split(' ')
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .toUpperCase();

const formatStamp = (value) => {
    if (!value) return '---';
    return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const byOldestFirst = (a, b) =>
    new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();

const byNewestFirst = (a, b) =>
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();

const SubtaskTag = ({ subtask }) => (
    <span
        className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 max-w-[14rem] truncate"
        title={subtask?.title}>
        {subtask?.title || 'Subtask'}
    </span>
);

const SubtaskPicker = ({ label, subtasks, targetId, onTargetChange }) => {
    if (subtasks.length <= 1) return null;
    return (
        <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
            <span className="whitespace-nowrap">{label}</span>
            <select
                value={targetId ?? ''}
                // The page compares subtask ids with ===, and a select hands back
                // a string. Coerce, or Add Document loses its subtask.
                onChange={(e) => onTargetChange?.(Number(e.target.value))}
                className="flex-1 border rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#346C75]">
                {subtasks.map((st) => (
                    <option key={st.id} value={st.id}>
                        {st.title}
                    </option>
                ))}
            </select>
        </label>
    );
};

const EmptyFeed = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <img src={noDataIcon} alt="No Data" className="w-24 h-24 opacity-50 mb-4" />
        <p>{message}</p>
    </div>
);

/**
 * One place for everything written against a task's subtasks. The read APIs are
 * per-subtask (subTask_id is required), so the merge happens here: one query per
 * subtask, flattened into a single chronological feed with the subtask named on
 * each row. Writes still target a single subtask, hence the picker.
 */
const TaskActivityPanel = ({ task, tab, targetId, onTargetChange, onOpenAddDoc }) => {
    const queryClient = useQueryClient();
    const subtasks = React.useMemo(() => task?.subTask || [], [task]);

    const [isAddExpenseOpen, setIsAddExpenseOpen] = React.useState(false);
    const [commentingOnId, setCommentingOnId] = React.useState(null);
    const [commentText, setCommentText] = React.useState('');

    // The target is owned by the page (the Add Document modal reads it too), so
    // normalise here and report up: as subtasks load, get added, or get deleted a
    // stale id must not leave the composer writing nowhere.
    React.useEffect(() => {
        if (!subtasks.length) {
            if (targetId != null) onTargetChange?.(null);
            return;
        }
        const valid = subtasks.some((st) => String(st.id) === String(targetId));
        if (!valid) onTargetChange?.(subtasks[0].id);
    }, [subtasks, targetId, onTargetChange]);

    const feedFor = (key, fetcher, wantedTab) =>
        subtasks.map((st) => ({
            queryKey: [key, st.id],
            queryFn: () => fetcher(st.id),
            enabled: tab === wantedTab && !!st.id
        }));

    const logResults = useQueries({ queries: feedFor('taskLogs', getTaskLogs, 'Logs') });
    const expenseResults = useQueries({
        queries: feedFor('taskExpenses', getSubTaskExpenses, 'Expense')
    });

    const merge = (results, compare = byOldestFirst) =>
        results
            .flatMap((result, index) =>
                (result.data?.data || []).map((row) => ({ row, subtask: subtasks[index] }))
            )
            .sort((a, b) => compare(a.row, b.row));

    const isFeedLoading = (results) => results.some((result) => result.isLoading);

    const logs = merge(logResults, byNewestFirst);
    const expenses = merge(expenseResults);

    const docs = subtasks.flatMap((st) =>
        (st.docs || []).map((doc) => ({ row: doc, subtask: st }))
    );

    const invalidateFeed = (key) => {
        subtasks.forEach((st) => queryClient.invalidateQueries({ queryKey: [key, st.id] }));
    };

    const { mutate: addComment, isPending: isAddingComment } = useMutation({
        mutationFn: createSubTaskLogReply,
        onSuccess: () => {
            setCommentingOnId(null);
            setCommentText('');
            invalidateFeed('taskLogs');
            toast.success('Comment added');
        },
        onError: (err) => toast.error(err?.response?.data?.message || 'Failed to add comment')
    });

    const refreshSpend = () => {
        invalidateFeed('taskExpenses');
        queryClient.invalidateQueries({ queryKey: ['managementTask'] });
    };

    const { mutate: approveSpend } = useMutation({
        mutationFn: approveSubTaskExpense,
        onSuccess: () => {
            refreshSpend();
            toast.success('Spend approved');
        },
        onError: (err) => toast.error(err?.response?.data?.message || 'Failed to approve spend')
    });

    const { mutate: voidSpend } = useMutation({
        mutationFn: voidSubTaskExpense,
        onSuccess: () => {
            refreshSpend();
            toast.success('Spend cancelled');
        },
        onError: (err) => toast.error(err?.response?.data?.message || 'Failed to cancel spend')
    });

    const canActOnSpend = !isExternalSession();
    const isOwner = actsAsOwner(task);
    const spendRule = evaluateSpendApprove({ isOwner, isExternal: !canActOnSpend });

    const handleApproveSpend = (expense, subtask) => {
        if (!spendRule.allowed) {
            toast.error(spendRule.message || SPEND_OWNER_MESSAGE);
            return;
        }
        if (lineExceedsAllocation(subtask, expense.amount) && isOwner) {
            const ok = window.confirm('This line exceeds remaining allocation. Approve as owner?');
            if (!ok) return;
        }
        approveSpend(expense.id);
    };

    const handleAddComment = (logId) => {
        if (!commentText.trim() || !logId || isAddingComment) return;
        addComment({ logId, reply: commentText });
    };

    if (!subtasks.length) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
                <EmptyFeed message="Add a subtask first. Logs, expenses and documents all hang off subtasks." />
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5">
            {tab === 'Logs' && (
                <>
                    {isFeedLoading(logResults) ? (
                        <Loader isVisible={true} />
                    ) : logs.length === 0 ? (
                        <EmptyFeed message="Nothing logged yet" />
                    ) : (
                        <ul className="divide-y divide-gray-100 -mx-5 -mt-1">
                            {logs.map(({ row: log, subtask }) => {
                                const replies = log.replies || [];
                                const isSystem = log.kind === 'system';
                                return (
                                    <li key={log.id} className="px-5 py-4">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-xs text-gray-500">
                                                {formatStamp(log.createdAt)}
                                            </span>
                                            <SubtaskTag subtask={subtask} />
                                            {isSystem ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EBF1F2] text-[#346C75]">
                                                    {log.eventType || 'auto'}
                                                </span>
                                            ) : (
                                                <span
                                                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                        log.shared
                                                            ? 'bg-teal-50 text-teal-800'
                                                            : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {log.shared ? 'Shared' : 'Internal'}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-2 text-sm text-gray-800">{log.log}</p>
                                        <p className="mt-1 text-xs text-gray-500">
                                            By {log.user?.name || 'Unknown'}
                                        </p>
                                        {replies.length > 0 && (
                                            <div className="mt-3 space-y-2 border-l-2 border-gray-100 pl-3">
                                                {replies.map((reply) => (
                                                    <div
                                                        key={reply.id}
                                                        className="flex items-start gap-2">
                                                        <div className="w-6 h-6 rounded-full bg-[#EBF1F2] flex items-center justify-center text-[#346C75] font-medium text-[10px] flex-shrink-0">
                                                            {getInitials(reply.user?.name)}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-xs font-semibold text-gray-800">
                                                                    {reply.user?.name || 'Unknown'}
                                                                </span>
                                                                <span className="text-[11px] text-gray-400">
                                                                    {formatStamp(reply.createdAt)}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-600">
                                                                {reply.log}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {commentingOnId === log.id ? (
                                            <div className="mt-3 flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    value={commentText}
                                                    onChange={(e) => setCommentText(e.target.value)}
                                                    placeholder="Write a comment..."
                                                    className="flex-1 min-w-0 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#346C75] bg-gray-50"
                                                    onKeyDown={(e) =>
                                                        e.key === 'Enter' && handleAddComment(log.id)
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddComment(log.id)}
                                                    disabled={!commentText.trim() || isAddingComment}
                                                    className={`p-1.5 rounded-full text-white flex-shrink-0 ${
                                                        commentText.trim() && !isAddingComment
                                                            ? 'bg-[#346C75] hover:bg-[#2a565e]'
                                                            : 'bg-[#346C75] bg-opacity-50 cursor-not-allowed'
                                                    }`}>
                                                    <Send size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setCommentingOnId(null);
                                                        setCommentText('');
                                                    }}
                                                    className="text-xs text-gray-500 hover:text-gray-700 flex-shrink-0">
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCommentingOnId(log.id);
                                                    setCommentText('');
                                                }}
                                                className="mt-2 text-xs font-medium text-[#346C75] hover:underline">
                                                Comment
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}

            {tab === 'Expense' && (
                <>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-gray-700">Spend ledger</h3>
                        {canActOnSpend && (
                            <button
                                type="button"
                                onClick={() => setIsAddExpenseOpen(true)}
                                className="flex items-center text-sm bg-[#346C75] text-white px-3 py-1.5 rounded hover:bg-[#2a565e] transition-colors">
                                <Plus size={16} className="mr-1" /> Add Expense
                            </button>
                        )}
                    </div>

                    <div className="max-h-[26rem] overflow-y-auto pr-1">
                        {isFeedLoading(expenseResults) ? (
                            <Loader isVisible={true} />
                        ) : expenses.length === 0 ? (
                            <EmptyFeed message="No expenses yet" />
                        ) : (
                            <div className="space-y-3">
                                {expenses.map(({ row: expense, subtask }) => {
                                    const badge = spendBadge(expense);
                                    const status =
                                        expense.ledgerStatus || (expense.paid ? 'paid' : 'draft');
                                    return (
                                        <div
                                            key={expense.id}
                                            className="bg-white p-3 rounded border border-gray-100 shadow-sm flex justify-between items-center gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-semibold text-gray-800">
                                                        {formatRupees(expense.amount)}
                                                    </p>
                                                    <SubtaskTag subtask={subtask} />
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    {formatStamp(expense.createdAt)}
                                                </p>
                                                {(expense.user?.name || expense.user?.userName) && (
                                                    <p className="text-xs text-gray-500">
                                                        Added by{' '}
                                                        {expense.user?.name ||
                                                            expense.user?.userName}
                                                    </p>
                                                )}
                                                {expense.note && (
                                                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap break-words">
                                                        {expense.note}
                                                    </p>
                                                )}
                                                {expense.bill?.url && (
                                                    <a
                                                        href={expense.bill.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1 mt-2 text-xs font-medium text-[#346C75] bg-[#EBF1F2] px-2 py-1 rounded hover:bg-[#dbe7e9] w-fit transition-colors"
                                                        title={expense.bill.name}>
                                                        <FileText size={12} />
                                                        View Bill
                                                    </a>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-medium ${badge.className}`}>
                                                    {badge.label}
                                                </span>
                                                {status === 'draft' && canActOnSpend && (
                                                    <div className="flex gap-1">
                                                        {spendRule.allowed && (
                                                            <button
                                                                type="button"
                                                                className="text-xs text-white bg-[#346C75] px-2 py-0.5 rounded"
                                                                onClick={() =>
                                                                    handleApproveSpend(
                                                                        expense,
                                                                        subtask
                                                                    )
                                                                }>
                                                                Approve
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded"
                                                            onClick={() => voidSpend(expense.id)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {isAddExpenseOpen &&
                        createPortal(
                            <Modal
                                width={30}
                                name="Add Expenses"
                                onClose={() => setIsAddExpenseOpen(false)}>
                                <div className="px-1 pb-2">
                                    <SubtaskPicker
                                        label="Expense on"
                                        subtasks={subtasks}
                                        targetId={targetId}
                                        onTargetChange={onTargetChange}
                                    />
                                </div>
                                <AddExpenseModal
                                    subTaskId={targetId}
                                    draftOnly={true}
                                    onSuccess={() => {
                                        setIsAddExpenseOpen(false);
                                        invalidateFeed('taskExpenses');
                                        queryClient.invalidateQueries({
                                            queryKey: ['managementTask']
                                        });
                                    }}
                                    onClose={() => setIsAddExpenseOpen(false)}
                                />
                            </Modal>,
                            document.body
                        )}
                </>
            )}

            {tab === 'Documents' && (
                <>
                    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                        <SubtaskPicker
                            label="Attach to"
                            subtasks={subtasks}
                            targetId={targetId}
                            onTargetChange={onTargetChange}
                        />
                        <button
                            type="button"
                            onClick={() => onOpenAddDoc?.(targetId)}
                            className="ml-auto shrink-0 px-4 py-2 bg-[#346C75] text-white font-medium text-sm rounded hover:bg-[#2a565e] transition-colors">
                            + Add Document
                        </button>
                    </div>
                    <div>
                        {docs.length === 0 ? (
                            <EmptyFeed message="No documents attached" />
                        ) : (
                            <div className="space-y-3">
                                {docs.map(({ row: doc, subtask }, index) => (
                                    <div
                                        key={doc.id || `${subtask?.id}-${index}`}
                                        className="flex justify-between items-center bg-white p-3 rounded border border-gray-100 shadow-sm">
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className="bg-red-50 p-2 rounded text-red-500 flex-shrink-0">
                                                <FileText size={20} />
                                            </div>
                                            <div className="truncate">
                                                <div className="flex items-center gap-2">
                                                    <p
                                                        className="font-medium text-sm text-gray-800 truncate"
                                                        title={doc.name}>
                                                        {doc.name || 'Untitled Document'}
                                                    </p>
                                                    <SubtaskTag subtask={subtask} />
                                                </div>
                                                <a
                                                    href={doc.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-blue-500 hover:underline truncate block">
                                                    View Document
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TaskActivityPanel;
